import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../features/auth/AuthContext.jsx';
import { PERMISSIONS } from '../lib/permissions.js';
import { Button } from './ui.jsx';
import { initials, humanise } from '../lib/format.js';
import logo from '../assets/grihvaas-logo.png';

/**
 * Nav and chrome icons.
 *
 * Drawn inline rather than loaded as an icon font: the Stitch design uses
 * Material Symbols, but pulling a ~150KB font over the network for six glyphs
 * would cost more than it saves, and the rest of this panel already draws its
 * icons this way.
 */
const ICONS = {
  dashboard: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
    </svg>
  ),
  agents: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  ),
  users: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  ),
  brokers: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 3 2 8v2h20V8l-10-5zM4 12v6H2v2h20v-2h-2v-6h-2v6h-3v-6h-2v6H8v-6H4z" />
    </svg>
  ),
  notifications: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6v-5a6 6 0 0 0-5-5.91V4a1 1 0 0 0-2 0v1.09A6 6 0 0 0 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
  ),
  support: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-5 4V6a2 2 0 0 1 1-2zm4 5v2h8V9H8zm0 4v2h5v-2H8z" />
    </svg>
  ),
  banners: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" />
    </svg>
  ),
  reports: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14.4 6 14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6z" />
    </svg>
  ),
  activity: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.5 18.49 9.5 12.48l4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />
    </svg>
  ),
  settings: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" />
    </svg>
  ),
  audit: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
    </svg>
  ),
  payments: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
    </svg>
  ),
  credits: (
    <svg className="sidebar__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4H4V6h16v2zm0 10H4v-6h16v6zm-9-4h7v2h-7v-2z" />
    </svg>
  ),
};

const SEARCH_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const BELL_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  </svg>
);

/**
 * The application frame: a persistent sidebar and a scrolling main area.
 *
 * Navigation is filtered by permission, so an admin only sees the sections
 * they can actually open. The server enforces the same permissions — this just
 * avoids offering a door that will not open.
 */

const SECTIONS = [
  {
    title: null,
    links: [
      { to: '/', label: 'Dashboard', icon: 'dashboard', end: true, permission: PERMISSIONS.DASHBOARD_VIEW },
      { to: '/agents', label: 'Agents', icon: 'agents', permission: PERMISSIONS.AGENT_VIEW },
      { to: '/users', label: 'Users', icon: 'users', permission: PERMISSIONS.USER_VIEW },
      { to: '/brokers', label: 'Brokers', icon: 'brokers', permission: PERMISSIONS.BROKER_VIEW },
      {
        to: '/inventory',
        label: 'Inventory',
        icon: 'brokers',
        permission: PERMISSIONS.BROKER_PROPERTY_VIEW,
      },
      { to: '/reports', label: 'Reports', icon: 'reports', permission: PERMISSIONS.MODERATION_VIEW },
      { to: '/payments', label: 'Payments', icon: 'payments', permission: PERMISSIONS.FINANCE_VIEW },
      { to: '/activity', label: 'Activity', icon: 'activity', permission: PERMISSIONS.LEAD_VIEW },
      {
        to: '/notifications',
        label: 'Notifications',
        icon: 'notifications',
        permission: PERMISSIONS.NOTIFICATION_SEND,
      },
      { to: '/support', label: 'Support', icon: 'support', permission: PERMISSIONS.SUPPORT_VIEW },
      { to: '/banners', label: 'Banners', icon: 'banners', permission: PERMISSIONS.CONFIG_VIEW },
      {
        to: '/credits',
        label: 'Credits',
        icon: 'credits',
        // Finance reads the ledger, operations the ads and the prices.
        permission: [
          PERMISSIONS.FINANCE_VIEW,
          PERMISSIONS.BROKER_PROPERTY_VIEW,
          PERMISSIONS.CONFIG_VIEW,
        ],
      },
      { to: '/settings', label: 'Settings', icon: 'settings', permission: PERMISSIONS.CONFIG_VIEW },
      { to: '/audit-log', label: 'Audit log', icon: 'audit', permission: PERMISSIONS.AUDIT_VIEW },
    ],
  },
];

export default function AppShell() {
  const { admin, signOut, canAny } = useAuth();

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <img className="sidebar__mark" src={logo} alt="" />
          <span className="sidebar__name">Grihvaas</span>
        </div>

        <nav className="sidebar__nav">
          {SECTIONS.map((section) => {
            const visible = section.links.filter((link) =>
              canAny(...(Array.isArray(link.permission) ? link.permission : [link.permission])),
            );
            if (!visible.length) return null;

            return (
              <div key={section.title || 'root'}>
                {section.title ? <div className="sidebar__section">{section.title}</div> : null}
                {visible.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) =>
                      `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
                    }
                  >
                    {ICONS[link.icon]}
                    {link.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

      </aside>

      <main className="main">
        <header className="topbar">
          <div className="topbar__search">
            {SEARCH_ICON}
            <input type="search" placeholder="Search records..." aria-label="Search records" />
          </div>

          <div className="topbar__right">
            <button type="button" className="topbar__bell" aria-label="Notifications">
              {BELL_ICON}
              <span className="topbar__dot" />
            </button>

            <div className="topbar__admin">
              <div className="topbar__admin-text">
                <div className="topbar__admin-name">{admin?.name}</div>
                <div className="topbar__admin-role">{humanise(admin?.role)}</div>
              </div>
              <div className="topbar__avatar">{initials(admin?.name)}</div>
              <Button variant="outline" size="sm" onClick={signOut}>
                Sign out
              </Button>
            </div>
          </div>
        </header>

        <Outlet />
      </main>
    </div>
  );
}

/** Page chrome, so every screen has the same header shape. */
export function Page({ title, subtitle, actions, children }) {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          {subtitle ? <p className="page-header__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="row">{actions}</div> : null}
      </header>
      <div className="page-body">{children}</div>
    </>
  );
}
